/**
 * Derivation module
 *
 * Computes per-contract economics empirically from the statement's OWN numbers
 * — no hardcoded CME contract specs or commission tiers:
 *
 *   $/point  = realizedPnL / ((avgSellPrice - avgBuyPrice) * matchedQty)   [per P&S block]
 *   tickSize = smallest observed price increment for the contract
 *   tickValue = pointValue * tickSize
 *   fee/contract = total fees / total contract-sides traded               [from confirmations]
 *
 * Per-contract pointValue is the MEDIAN of per-block $/point (robust to the odd
 * block skewed by rounding or partial matches).
 */

import type {
  ConfirmationBlock, PsBlock, ContractEconomics, FeeBreakdown, AccountSummary, Reconciliation, CashFlow,
  Trade, FifoPosition, OpenPosition,
} from './types';

export function aggregateFees(parts: FeeBreakdown[]): FeeBreakdown {
  const agg: FeeBreakdown = {
    lines: [], exchange: 0, nfa: 0, clearing: 0, cqgTrf: 0, commission: 0, other: 0, total: 0,
  };
  for (const p of parts) {
    agg.lines.push(...p.lines);
    agg.exchange += p.exchange;
    agg.nfa += p.nfa;
    agg.clearing += p.clearing;
    agg.cqgTrf += p.cqgTrf;
    agg.commission += p.commission;
    agg.other += p.other;
    agg.total += p.total;
  }
  agg.exchange = round(agg.exchange);
  agg.nfa = round(agg.nfa);
  agg.clearing = round(agg.clearing);
  agg.cqgTrf = round(agg.cqgTrf);
  agg.commission = round(agg.commission);
  agg.other = round(agg.other);
  agg.total = round(agg.total);
  return agg;
}

export function computeEconomics(
  confirmations: ConfirmationBlock[], psBlocks: PsBlock[]
): ContractEconomics[] {
  const symbols = new Set<string>();
  confirmations.forEach(b => symbols.add(b.contractType));
  psBlocks.forEach(b => symbols.add(b.contractType));

  const out: ContractEconomics[] = [];
  for (const sym of symbols) {
    if (!sym) continue;
    const confs = confirmations.filter(b => b.contractType === sym);
    const ps = psBlocks.filter(b => b.contractType === sym);

    // $/point per block (futures only)
    const pvSamples: number[] = [];
    for (const b of ps) {
      const isFuture = (b.fills[0]?.Instrument_Type ?? 'FUTURE') === 'FUTURE';
      const spread = b.avgSellPrice - b.avgBuyPrice;
      if (!isFuture || !b.matchedQuantity || Math.abs(spread) < 1e-9) { b.pointValue = null; continue; }
      const pv = Math.abs(b.realizedPnL / (spread * b.matchedQuantity));
      if (isFinite(pv) && pv > 1e-4 && pv < 1e7) { b.pointValue = round1(pv); pvSamples.push(pv); }
      else b.pointValue = null;
    }

    const tickSize = smallestIncrement([
      ...confs.flatMap(b => b.fills.map(f => f.Price_Num)),
      ...ps.flatMap(b => b.fills.map(f => f.Price_Num)),
    ]);
    const pointValue = pvSamples.length ? round1(median(pvSamples)) : null;
    const tickValue = pointValue != null && tickSize != null ? round1(pointValue * tickSize) : null;

    const totalSides = confs.reduce((s, b) => s + b.buyQty + b.sellQty, 0);
    const totalFees = confs.reduce((s, b) => s + b.fees.total, 0);
    const feePerContract = totalSides ? round1(totalFees / totalSides) : null;
    const totalRealizedPnL = round(ps.reduce((s, b) => s + b.realizedPnL, 0));

    out.push({
      contractType: sym,
      pointValue,
      tickSize,
      tickValue,
      feePerContract,
      totalContracts: totalSides,
      totalRealizedPnL,
      totalFees: round(totalFees),
      sampleBlocks: pvSamples.length,
    });
  }
  return out.sort((a, b) => a.contractType.localeCompare(b.contractType));
}

/**
 * FIFO position matching, grouped by contract AND expiration (so different
 * contract months of the same symbol are never matched against each other).
 * Executions are processed in execution-id order; each fill offsets the oldest
 * open lot of the opposite side, closing a position. Realized P&L uses the
 * empirically-derived $/point for the symbol.
 *
 * When a contract/expiration ends flat, the sum of closed P&L equals the
 * statement's realized P&L regardless of matching order. Any fills left
 * unmatched at the end are returned as OPEN positions (a position carried out,
 * or a close of a position carried in from a prior month).
 */
export function computeFifoPositions(
  trades: Trade[], economics: ContractEconomics[]
): { closed: FifoPosition[]; open: OpenPosition[] } {
  const pvBySymbol = new Map(economics.map(e => [e.contractType, e.pointValue]));
  const feeBySymbol = new Map(economics.map(e => [e.contractType, e.feePerContract]));

  // Group by symbol + expiration (fall back to month when expiration is blank).
  const groups = new Map<string, Trade[]>();
  for (const t of trades) {
    const key = `${t.Contract_Type}||${t.Expiration || t.Contract_Month}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(t);
  }

  const closed: FifoPosition[] = [];
  const open: OpenPosition[] = [];

  type Lot = { side: 'BUY' | 'SELL'; qty: number; price: number; id: string; date: string };
  for (const fills of groups.values()) {
    const symbol = fills[0].Contract_Type;
    const month = fills[0].Contract_Month;
    const expiration = fills[0].Expiration;
    const pv = pvBySymbol.get(symbol) ?? null;
    const feeSide = feeBySymbol.get(symbol) ?? 0;
    const sorted = [...fills].sort((a, b) => Number(a.Trade_Number) - Number(b.Trade_Number));
    const lots: Lot[] = [];

    for (const f of sorted) {
      let qty = f.Quantity;
      while (qty > 0 && lots.length && lots[0].side !== f.Direction) {
        const lot = lots[0];
        const m = Math.min(qty, lot.qty);
        const direction: 'LONG' | 'SHORT' = lot.side === 'BUY' ? 'LONG' : 'SHORT';
        const points = direction === 'LONG' ? f.Price_Num - lot.price : lot.price - f.Price_Num;
        const gross = pv != null ? round(points * m * pv) : null;
        const commission = round((feeSide || 0) * m * 2);
        closed.push({
          account: f.Account, contractType: symbol, contractMonth: month, direction,
          quantity: m,
          entryId: lot.id, entryDate: lot.date, entryPrice: lot.price,
          exitId: f.Trade_Number, exitDate: f.Date, exitPrice: f.Price_Num,
          points: round1(points), pointValue: pv, grossPnL: gross, commission,
          netPnL: gross == null ? null : round(gross - commission),
        });
        lot.qty -= m; qty -= m;
        if (lot.qty === 0) lots.shift();
      }
      if (qty > 0) lots.push({ side: f.Direction, qty, price: f.Price_Num, id: f.Trade_Number, date: f.Date });
    }

    // Whatever remains is an open position (all lots are the same side by now).
    if (lots.length) {
      const totalQty = lots.reduce((s, l) => s + l.qty, 0);
      const avgPrice = round1(lots.reduce((s, l) => s + l.price * l.qty, 0) / totalQty);
      open.push({
        account: fills[0].Account, contractType: symbol, contractMonth: month, expiration,
        direction: lots[0].side === 'BUY' ? 'LONG' : 'SHORT',
        quantity: totalQty, avgPrice, pointValue: pv,
        lots: lots.map(l => ({ id: l.id, date: l.date, quantity: l.qty, price: l.price })),
      });
    }
  }

  closed.sort((a, b) => Number(a.exitId) - Number(b.exitId));
  return { closed, open };
}

export function reconcile(
  psBlocks: PsBlock[], fees: FeeBreakdown, cashFlow: CashFlow, summary: AccountSummary | null
): Reconciliation {
  const psSum = round(psBlocks.reduce((s, b) => s + b.realizedPnL, 0));
  const summaryPnL = summary?.realizedPnL ?? null;
  const summaryComm = summary?.commission?.total ?? null;
  const summaryPay = summary?.paymentsReceipts ?? null;
  const tol = 0.02;
  return {
    psBlockPnLSum: psSum,
    summaryRealizedPnL: summaryPnL,
    pnlMatches: summaryPnL == null ? null : Math.abs(psSum - summaryPnL) <= tol,
    blockFeesTotal: round(fees.total),
    summaryCommissionTotal: summaryComm,
    feesMatch: summaryComm == null ? null : Math.abs(Math.abs(fees.total) - summaryComm) <= tol,
    cashFlowNet: round(cashFlow.net),
    summaryPaymentsReceipts: summaryPay,
    cashFlowMatches: summaryPay == null ? null : Math.abs(cashFlow.net - summaryPay) <= tol,
    flat: true,                 // set by the caller once open positions are known
    openTradeEquity: summary?.openTradeEquity ?? null,
  };
}

// --- numeric helpers ---

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Smallest positive gap between distinct sorted prices (the tick size). */
function smallestIncrement(prices: number[]): number | null {
  const uniq = [...new Set(prices.filter(p => p > 0).map(p => Math.round(p * 1e5) / 1e5))]
    .sort((a, b) => a - b);
  if (uniq.length < 2) return null;
  let min = Infinity;
  for (let i = 1; i < uniq.length; i++) {
    const d = uniq[i] - uniq[i - 1];
    if (d > 1e-6 && d < min) min = d;
  }
  return isFinite(min) ? round1(min) : null;
}

function round(v: number): number { return Math.round(v * 100) / 100; }
function round1(v: number): number { return Math.round(v * 1e5) / 1e5; }
